"use client";

import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useIsMobileDevice } from '@/hooks/use-mobile';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles 
}) => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { isInMaintenance, canBypassMaintenance } = useMaintenanceMode();
  const isMobileDevice = useIsMobileDevice();
  const navigate = useNavigate();
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      console.log('ProtectedRoute - Auth state:', { isLoading, isAuthenticated, user, allowedRoles, isInMaintenance, canBypassMaintenance, isMobileDevice });
      
      if (!isLoading && !isAuthenticated) {
        console.log('ProtectedRoute - Redirecting to login');
        navigate('/auth/login');
        return;
      }

      // Safety check: if user is authenticated but email not confirmed, redirect to verify-email
      if (!isLoading && isAuthenticated && user && !user.email_confirmed_at) {
        console.log('ProtectedRoute - Email not confirmed, redirecting to verify-email');
        navigate(`/auth/verify-email?email=${encodeURIComponent(user.email)}`);
        return;
      }

      // Check maintenance mode - if active and user cannot bypass, log them out
      if (!isLoading && isAuthenticated && user && isInMaintenance && !canBypassMaintenance) {
        console.log('ProtectedRoute - Maintenance mode active, non-admin user, logging out');
        await logout();
        navigate('/auth/login');
        return;
      }

      // Check for mobile device - redirect to desktop-required page (allow admins to bypass)
      if (!isLoading && isAuthenticated && user && isMobileDevice && user.role !== 'admin') {
        // Check if mobile restriction feature flag is enabled
        const mobileRestrictionEnabled = await featureFlags.isEnabled(
          FEATURE_FLAGS.MOBILE_DEVICE_RESTRICTION
        );
        
        if (mobileRestrictionEnabled) {
          console.log('ProtectedRoute - Mobile device detected, redirecting to desktop-required');
          navigate('/desktop-required');
          return;
        } else {
          console.log('ProtectedRoute - Mobile device detected but restriction is disabled via feature flag');
        }
      }
      
      if (allowedRoles && user && !allowedRoles.includes(user.role)) {
        console.log('ProtectedRoute - Access denied, redirecting to unauthorized');
        navigate('/unauthorized');
        return;
      }

      setIsCheckingAccess(false);
    };

    checkAccess();
  }, [isAuthenticated, isLoading, user, allowedRoles, navigate, isInMaintenance, canBypassMaintenance, logout, isMobileDevice]);

  // Wait for auth AND role to be fully loaded to prevent role confusion
  if (isLoading || isCheckingAccess || (isAuthenticated && !user) || (isAuthenticated && user && !user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};